/* eslint-disable @typescript-eslint/no-explicit-any */
import log from 'loglevel';

const DEFAULT_APP_NAME = 'BIZ-NEST';
const DEFAULT_LOG_LEVEL: log.LogLevelDesc = 'debug';

/**
 * The FrontendLogger class provides a robust logging solution for frontend applications.
 * It integrates with 'loglevel' for efficient level management and named loggers,
 * and customizes console output to include formatted prefixes with colors.
 *
 * This implementation directly leverages 'loglevel's `methodFactory` to control
 * the arguments passed to the native `console.log` methods, ensuring full control
 * over text and color formatting without relying on external, unmaintained plugins.
 */
export class FrontendLogger {
  // Static property to store the application name, allowing dynamic changes
  private static appName: string = DEFAULT_APP_NAME;

  // Static instance of a named 'loglevel' logger for internal use
  private static internalLogger = log.getLogger('appLogger');

  /**
   * Static initialization block. This code runs once when the FrontendLogger
   * module is first loaded/imported into the application.
   * It is used to customize how 'loglevel' creates its logging methods (trace, debug, etc.).
   */
  static {
    // Store a reference to 'loglevel's original `methodFactory`.
    // This allows us to get the default `console` method (e.g., `console.log`, `console.warn`)
    // that `loglevel` would normally use, before we add our custom formatting.
    const originalMethodFactory = FrontendLogger.internalLogger.methodFactory;

    // Override the `methodFactory` for our `internalLogger` instance.
    // This custom factory will be invoked by 'loglevel' whenever it needs to create
    // or re-create its logging methods (e.g., when `setLevel` is called).
    FrontendLogger.internalLogger.methodFactory = (
      methodName: log.LogLevelNames, // The name of the log method (e.g., 'debug', 'info')
      logLevel: log.LogLevelNumbers, // The numeric level of the current log method
      loggerName: string | symbol // The name of the logger instance (can be a string or a symbol for root)
    ) => {
      // Use the original factory to get the actual underlying `console` method.
      // 'loglevel' handles internal level filtering here; if `logLevel` is below the logger's
      // current set level, `rawConsoleMethod` might be a no-op function.
      const rawConsoleMethod = originalMethodFactory(methodName, logLevel, loggerName);

      // Return our custom function that will be executed when a log method (e.g., `FrontendLogger.debug()`) is called.
      return (...messageArgs: any[]) => {
        // If `rawConsoleMethod` is a no-op (due to log level filtering), just return.
        if (!rawConsoleMethod) {
          return;
        }

        // --- Prepare components for the log prefix ---
        const timestamp = new Date().toISOString();
        // Use the currently configured appName for display in the prefix.
        const effectiveAppName = FrontendLogger.appName;

        // Ensure the method name (e.g., 'debug') is uppercase for display.
        const levelPart = typeof methodName === 'string' ? methodName.toUpperCase() : String(methodName).toUpperCase();

        // --- Construct the formatted prefix string with %c placeholders ---
        // Each `%c` in the string indicates that the next argument passed to `console.log`
        // will be a CSS style string to apply to the text segment preceding that `%c`.
        // The final `%c` without a preceding segment acts as a reset for the main message.
        // A space is added after the final %c for better visual separation.
        const formattedPrefixString = `%c[${timestamp}] %c${effectiveAppName}:%c${levelPart}%c `;

        // --- Prepare the array of CSS style strings corresponding to each %c ---
        const prefixStylesArray = [
          'color: gray;', // Style for the timestamp part `[${timestamp}]`
          'color:rgb(140, 199, 203); font-weight: bold;', // Style for the app name part `${effectiveAppName}:`
          FrontendLogger.getLevelStyle(logLevel), // Dynamic style for the log level part `${levelPart}`
          FrontendLogger.getLineBackgroundColor(logLevel) // Background style for the main message content
        ];

        // --- Call the underlying console method with all arguments ---
        // We use spread syntax to pass the prepared prefix string, its styles,
        // and any additional `messageArgs` (the actual data to be logged) as separate arguments.
        rawConsoleMethod(formattedPrefixString, ...prefixStylesArray, ...messageArgs);
      };
    };

    // Crucial step: After overriding `methodFactory`, we must call `setLevel`.
    // This forces `loglevel` to re-initialize its log methods (`trace`, `debug`, `info`, etc.)
    // using our *newly defined* `methodFactory`. Setting it to the default level
    // ensures the initial setup applies our custom formatting.
    FrontendLogger.internalLogger.setLevel(DEFAULT_LOG_LEVEL);
  }

  /**
   * Configures the logger's application name and/or desired log level.
   * This method can be called dynamically at any point in the application lifecycle
   * to change the logger's behavior.
   *
   * @param name Optional: The new name of the application to be displayed in logs.
   * @param logLevel Optional: The new log level (e.g., 'debug', 'info', 'warn').
   */
  public static configure(name?: string, logLevel?: log.LogLevelDesc): void {
    let shouldReinitializeMethods = false;
    const currentLevel = FrontendLogger.internalLogger.getLevel();

    if (name && FrontendLogger.appName !== name) {
      FrontendLogger.appName = name;
      // If the app name changes, methods need to be reinitialized to capture the new name in their closures.
      shouldReinitializeMethods = true;
    }

    if (logLevel !== undefined && logLevel !== currentLevel) {
      // If the log level changes, `setLevel` itself will trigger `methodFactory` re-run.
      FrontendLogger.internalLogger.setLevel(logLevel);
    } else if (shouldReinitializeMethods) {
      // If only the app name changed (and level wasn't explicitly set),
      // we force `setLevel` to current level to trigger `methodFactory` re-run.
      FrontendLogger.internalLogger.setLevel(currentLevel);
    }
  }

  // --- Public Static Logging Methods ---
  // These methods are the public API for logging. They simply delegate to the
  // `internalLogger` instance, which is now configured with our custom formatting
  // via the `methodFactory` override.

  public static trace(...args: any[]): void {
    FrontendLogger.internalLogger.trace(...args);
  }

  public static debug(...args: any[]): void {
    FrontendLogger.internalLogger.debug(...args);
  }

  public static info(...args: any[]): void {
    FrontendLogger.internalLogger.info(...args);
  }

  public static warn(...args: any[]): void {
    FrontendLogger.internalLogger.warn(...args);
  }

  public static error(...args: any[]): void {
    FrontendLogger.internalLogger.error(...args);
  }

  // --- Private Static Helper Methods ---
  // These methods are internal implementation details and are placed at the end
  // for better readability of the class's public interface.

  /**
   * Helper to get the CSS style string for a given log level.
   * This returns the style for the LEVEL PART ITSELF (e.g., DEBUG label)
   * within the log prefix. It includes text color, background color, padding, and border-radius.
   */
  private static getLevelStyle(level: log.LogLevelDesc): string {
    const levelString = typeof level === 'string' ? level.toLowerCase() : String(level);

    switch (levelString) {
      case 'trace':
      case '0': return 'color: white; background-color: #9C27B0; padding: 2px 4px; border-radius: 3px;';
      case 'debug':
      case '1': return 'color: white; background-color: #2196F3; padding: 2px 4px; border-radius: 3px;';
      case 'info':
      case '2': return 'color: white; background-color: #4CAF50; padding: 2px 4px; border-radius: 3px;';
      case 'warn':
      case '3': return 'color: black; background-color: #FFEB3B; padding: 2px 4px; border-radius: 3px;';
      case 'error':
      case '4': return 'color: white; background-color: #F44336; padding: 2px 4px; border-radius: 3px;';
      case 'silent':
      case '5': return '';
      default: return '';
    }
  }

  /**
   * Helper to get a subtle background color for the *entire log line's content*
   * based on the log level. This helps visually distinguish different log types.
   */
  private static getLineBackgroundColor(level: log.LogLevelDesc): string {
    const levelString = typeof level === 'string' ? level.toLowerCase() : String(level);

    switch (levelString) {
      case 'trace':
      case '0': return 'background-color: #F3E5F5;'; // Light purple
      case 'debug':
      case '1': return 'background-color: #e3f2fd;'; // Lighter blue
      case 'info':
      case '2': return 'background-color: #e8f5e9;'; // Lighter green
      case 'warn':
      case '3': return 'background-color: #fffde7;'; // Lighter yellow
      case 'error':
      case '4': return 'background-color: #ffebee;'; // Lighter red
      default: return '';
    }
  }
}
